import { Link } from "react-router-dom";
import { ArrowLeft, Printer, CheckSquare, Square } from "@phosphor-icons/react";
import { useApi, useWorkspace, num, nzDate } from "@/lib/api";
import { useLoad } from "@/components/app/common";
import { toBreaks, checkKey, mineralLine } from "@/pages/Feeding";

const weekday = (d) => new Date(`${d}T00:00:00`).toLocaleDateString("en-NZ", { weekday: "long" });
const plural = (n, w) => `${n} ${w}${n === 1 ? "" : "s"}`;

function Status({ k, label, day }) {
  if (!k || !day.calc?.checklist?.some((c) => c.key === k)) return null;
  const done = day.checks?.[k];
  return (
    <span className={`inline-flex items-center gap-1 text-lg font-bold px-2 rounded ${done ? "bg-green-600 text-white" : "border-2 border-black"}`} data-testid={`staff-check-${checkKey(k)}`}>
      {done ? <CheckSquare size={20} weight="fill" /> : <Square size={20} />}{label}
    </span>
  );
}

function MobBlock({ r, i, day }) {
  const breaks = toBreaks(r).map((b, j) => ({ ...b, j })).filter((b) => b.paddock);
  const crops = (day.calc?.rows?.[i]?.supplements || []).filter((s) => s.method === "paddock");
  const calcBreaks = day.calc?.rows?.[i]?.breaks || [];
  return (
    <div className="sc-block border-2 border-black rounded-lg p-4" data-testid={`staff-mob-${i}`}>
      <div className="flex flex-wrap justify-between items-baseline gap-2 mb-3">
        <h3 className="text-3xl font-black">{r.mob || `Mob ${i + 1}`}</h3>
        <p className="text-xl font-bold" data-testid={`staff-mob-${i}-head`}>
          {r.am_count ? `AM ${num(r.am_count, 0)}` : ""}{r.am_count && r.pm_count ? " · " : ""}{r.pm_count ? `PM ${num(r.pm_count, 0)}` : ""}{!r.am_count && !r.pm_count ? "Head not set" : " head"}
        </p>
      </div>
      {breaks.length === 0 && !crops.length ? <p className="text-xl font-bold">No paddock set</p> : breaks.map((b) => (
        <div key={b.j} className="border-t border-black/30 py-2" data-testid={`staff-mob-${i}-break-${b.j}`}>
          <div className="flex flex-wrap items-baseline gap-x-4">
            <span className="text-lg font-semibold uppercase w-36">{b.label || "Break"}</span>
            <span className="text-4xl font-black">{b.paddock}</span>
            {b.area_used ? <span className="text-2xl font-bold">{num(b.area_used, 2)} ha</span> : null}
          </div>
          {b.races_note && <p className="text-xl mt-1" data-testid={`staff-mob-${i}-break-${b.j}-races`}><b>Races →</b> {b.races_note}</p>}
          <div className="mt-1"><Status k={calcBreaks[b.j]?.check} label="Paddock Setup" day={day} /></div>
        </div>))}
      {crops.map((s) => (
        <div key={s.feed_key} className="border-t border-black/30 py-2" data-testid={`staff-mob-${i}-crop-${s.feed_key}`}>
          <div className="flex flex-wrap items-baseline gap-x-4">
            <span className="text-lg font-semibold uppercase w-36">Crop</span>
            <span className="text-4xl font-black">{s.name}{s.crop_paddock ? ` · ${s.crop_paddock}` : ""}</span>
            {s.crop_area_ha != null && <span className="text-2xl font-bold">{num(s.crop_area_ha, 2)} ha</span>}
          </div>
          <p className="text-lg">{num(s.kgdm_per_head, 1)} kgDM/cow</p>
          <div className="mt-1"><Status k={s.check} label="Paddock Setup" day={day} /></div>
        </div>))}
      {r.notes && <p className="mt-2 text-xl border-t border-black/30 pt-2" data-testid={`staff-mob-${i}-notes`}>Note: {r.notes}</p>}
    </div>
  );
}

function WagonBlock({ wagon, day }) {
  const total = wagon.reduce((s, w) => s + w.as_fed_kg, 0);
  return (
    <section className="sc-block mt-6 border-4 border-black rounded-lg p-4" data-testid="staff-wagon">
      <div className="flex flex-wrap justify-between items-baseline gap-2">
        <h2 className="text-2xl font-black uppercase">Mixer / feed-out wagon</h2>
        <p className="text-sm font-semibold">Scale weight (as-fed kg)</p>
      </div>
      {wagon.length === 0 ? <p className="text-xl mt-3">No wagon feed today.</p> : <>
        {wagon.map((w) => (
          <div key={w.feed} className="border-t-2 border-black mt-3 pt-3" data-testid={`staff-wagon-${w.feed}`}>
            <p className="text-3xl font-black">{w.feed}: {num(w.as_fed_kg, 0)} kg</p>
            <p className="text-xl font-bold">{w.loads ? `${plural(w.loads, "load")} of ~${num(w.kg_per_load, 0)} kg` : "Wagon capacity not set"}</p>
            <div className="mt-1"><Status k={w.check} label="Feed on pad" day={day} /></div>
            <ul className="mt-1 text-xl">{w.mobs.map((m, k) => <li key={k}>→ {m.mob}{m.paddock ? ` (${m.paddock})` : ""}: <b>{num(m.as_fed_kg, 0)} kg</b></li>)}</ul>
          </div>))}
        {wagon.length > 1 && <p className="border-t-2 border-black mt-3 pt-3 text-3xl font-black" data-testid="staff-wagon-total">Total mix: {num(total, 0)} kg</p>}
      </>}
    </section>
  );
}

function OutsideMinerals({ minerals }) {
  if (!minerals.length) return null;
  return (
    <section className="sc-block mt-6 border-4 border-black rounded-lg p-4" data-testid="staff-minerals">
      <h2 className="text-2xl font-black uppercase">Minerals</h2>
      <ul className="mt-2 text-2xl">{minerals.map((m, k) => <li key={k} className="border-t-2 border-black py-2" data-testid={`staff-mineral-${k}`}><b>{mineralLine(m)}</b> · {m.method === "water" ? "water / trough" : "wagon"}</li>)}</ul>
    </section>
  );
}

function Ahead({ days }) {
  return (
    <section className="sc-block mt-6" data-testid="staff-ahead">
      <h2 className="text-2xl font-black uppercase mb-3">Next 3 days grazing</h2>
      <div className="grid sm:grid-cols-3 print:grid-cols-3 gap-3">
        {days.map((d) => (
          <div key={d.date} className="border-2 border-black rounded-lg p-3" data-testid={`staff-ahead-${d.date}`}>
            <p className="text-xl font-black">{weekday(d.date)}</p>
            <p className="text-sm mb-2">{nzDate(d.date)}{d.status ? "" : " · not planned"}</p>
            {d.rows.map((r, i) => (
              <div key={i} className="mb-2">
                <p className="font-bold text-lg">{r.mob || `Mob ${i + 1}`}</p>
                {toBreaks(r).filter((b) => b.paddock).map((b, j) => <p key={j} className="text-lg">{b.label || "Break"}: <b>{b.paddock}</b>{b.area_used ? ` ${num(b.area_used, 2)} ha` : ""}{b.races_note ? ` · races: ${b.races_note}` : ""}</p>)}
                {(d.calc?.rows?.[i]?.supplements || []).filter((s) => s.method === "paddock").map((s) => <p key={s.feed_key} className="text-lg">Crop: <b>{s.name}{s.crop_paddock ? ` ${s.crop_paddock}` : ""}</b>{s.crop_area_ha != null ? ` ${num(s.crop_area_ha, 2)} ha` : ""}</p>)}
              </div>))}
            {d.notes && <p className="text-base mt-1">Note: {d.notes}</p>}
          </div>))}
      </div>
    </section>
  );
}

export default function StaffCard() {
  const api = useApi();
  const { farm } = useWorkspace();
  const [days, , err] = useLoad(() => (farm ? api.get(`/feeding/upcoming?farm_id=${farm.id}&days=4`) : Promise.resolve(null)), [farm?.id]);
  const today = days?.[0];
  const list = today?.calc?.checklist || [];
  const ready = list.length > 0 && list.every((c) => today.checks?.[c.key]);
  return (
    <div className="staff-card min-h-screen bg-white text-black p-4 sm:p-8 max-w-5xl mx-auto" data-testid="staff-card">
      <div className="flex flex-wrap gap-3 justify-between mb-6 print:hidden">
        <Link to="/feeding" className="inline-flex items-center gap-2 text-lg font-semibold underline" data-testid="staff-card-back"><ArrowLeft size={20} />Back to Feeding</Link>
        <button onClick={() => window.print()} className="inline-flex items-center gap-2 bg-black text-white px-5 py-3 rounded-lg text-lg font-bold" data-testid="staff-card-print"><Printer size={22} />Print A4</button>
      </div>
      {!farm ? <p className="text-2xl">Pick a farm first.</p> : err ? <p className="text-2xl font-bold" data-testid="staff-card-error">Couldn't load the feed sheet: {err}</p> : !today ? <p className="text-2xl" data-testid="staff-card-loading">Loading…</p> : <>
        <header className="border-b-4 border-black pb-3 mb-5">
          <p className="text-lg font-semibold">{farm.name}</p>
          <h1 className="text-4xl sm:text-5xl font-black" data-testid="staff-card-date">{weekday(today.date)} {nzDate(today.date)}</h1>
          {today.status && <p className="text-lg font-bold mt-1" data-testid="staff-card-status">{today.status === "confirmed" ? "Confirmed" : "Plan"}</p>}
          {ready && <p className="mt-2 inline-block bg-green-600 text-white text-2xl font-black px-3 py-1 rounded" data-testid="staff-card-ready">Everything is ready</p>}
        </header>
        {today.notes && <p className="sc-block text-2xl font-bold border-4 border-black rounded-lg p-4 mb-5" data-testid="staff-day-notes">{today.notes}</p>}
        {!today.rows.length ? <p className="text-2xl font-bold" data-testid="staff-card-empty">No feed sheet set for today yet.</p> : <>
          <h2 className="text-2xl font-black uppercase mb-3">Break fences</h2>
          <div className="space-y-4">{today.rows.map((r, i) => <MobBlock key={i} r={r} i={i} day={today} />)}</div>
          <WagonBlock wagon={today.calc?.totals?.wagon || []} day={today} />
          <OutsideMinerals minerals={(today.calc?.totals?.minerals || []).filter((m) => m.method !== "in_shed")} />
        </>}
        <Ahead days={days.slice(1)} />
      </>}
    </div>
  );
}
